import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { setActiveCurrency } from '../utils/helpers';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

export const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', flag: '🇲🇽' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', flag: '🇰🇷' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty', flag: '🇵🇱' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', flag: '🇨🇿' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', flag: '🇭🇺' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu', flag: '🇷🇴' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', flag: '🇹🇷' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble', flag: '🇷🇺' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound', flag: '🇪🇬' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', flag: '��🇦' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '🇸🇬' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', flag: '🇭🇰' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', flag: '🇳🇿' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', flag: '🇲🇾' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', flag: '🇹🇭' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', flag: '🇮🇩' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', flag: '🇵🇭' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', flag: '🇻🇳' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', flag: '🇵🇰' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', flag: '🇧🇩' },
  { code: 'CLP', symbol: 'CL$', name: 'Chilean Peso', flag: '🇨🇱' },
  { code: 'COP', symbol: 'CO$', name: 'Colombian Peso', flag: '🇨🇴' },
  { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso', flag: '🇦🇷' },
  // African currencies
  { code: 'CDF', symbol: 'FC', name: 'Congolese Franc', flag: '🇨🇩' },
  { code: 'XAF', symbol: 'Fr', name: 'CFA Franc BEAC (Central Africa)', flag: '🌍' },
  { code: 'XOF', symbol: 'Fr', name: 'CFA Franc BCEAO (West Africa)', flag: '🌍' },
  { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr', flag: '🇪🇹' },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', flag: '🇹🇿' },
  { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', flag: '🇺🇬' },
  { code: 'RWF', symbol: 'RF', name: 'Rwandan Franc', flag: '🇷🇼' },
  { code: 'AOA', symbol: 'Kz', name: 'Angolan Kwanza', flag: '🇦🇴' },
];

// Shared formatter — used by both the context hook and the helpers.js singleton
export const formatWithCurrency = (amount, currency, showPositive = false, compact = false, showDecimals = true) => {
  const safeAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  const symbolMap = {
    EUR: '€', USD: '$', GBP: '£', JPY: '¥', CHF: 'Fr', CAD: 'CA$', AUD: 'A$',
    CNY: '¥', CDF: 'FC', XAF: 'Fr', XOF: 'Fr', GHS: '₵', KES: 'KSh',
  };
  const sym = symbolMap[currency] || currency;

  if (compact && Math.abs(safeAmount) >= 1000) {
    if (Math.abs(safeAmount) >= 1000000) return `${sym}${(safeAmount / 1000000).toFixed(1)}M`;
    return `${sym}${(safeAmount / 1000).toFixed(1)}K`;
  }

  let formatted;
  try {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    }).format(Math.abs(safeAmount));
  } catch {
    formatted = `${sym}${Math.abs(safeAmount).toFixed(showDecimals ? 2 : 0)}`;
  }

  if (showPositive && safeAmount > 0) return `+${formatted}`;
  return formatted;
};

const CurrencyContext = createContext({
  currency: 'EUR',
  currencies: CURRENCIES,
  updateCurrency: async () => {},
  getCurrencySymbol: () => '€',
  formatAmount: (amount) => `€${Math.abs(amount || 0).toFixed(2)}`,
});

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(
    () => localStorage.getItem('soldikeeper-currency') || 'EUR'
  );

  // Sync singleton immediately
  useEffect(() => {
    setActiveCurrency(currency);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user's preference from backend on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE_URL}/user-preferences`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const saved = data?.data?.currency || data?.currency;
        if (saved) {
          setCurrencyState(saved);
          setActiveCurrency(saved);
          localStorage.setItem('soldikeeper-currency', saved);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCurrency = useCallback(async (code) => {
    setCurrencyState(code);
    setActiveCurrency(code);
    localStorage.setItem('soldikeeper-currency', code);
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE_URL}/user-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currency: code })
      }).catch(() => {});
    }
  }, []);

  const getCurrencySymbol = useCallback((code) => {
    return CURRENCIES.find(c => c.code === (code || currency))?.symbol || '€';
  }, [currency]);

  // formatAmount closes over `currency` state → components using useCurrency() re-render on change
  const formatAmount = useCallback((amount, showPositive = false, compact = false, showDecimals = true) => {
    return formatWithCurrency(amount, currency, showPositive, compact, showDecimals);
  }, [currency]);

  const value = useMemo(() => ({
    currency,
    currencies: CURRENCIES,
    updateCurrency,
    getCurrencySymbol,
    formatAmount,
  }), [currency, updateCurrency, getCurrencySymbol, formatAmount]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
export default CurrencyContext;
